// Filter chip definitions — ordered by priority for responsive overflow.

export interface SearchFilters {
  from?: string;
  to?: string;
  agentName?: string;
  agentNameExactMatch?: boolean;
  agentId?: string;
  siteIds?: string[];
  interactionType?: string;
  tags?: string[];
  direction?: string;
  phoneNumber?: string;
  dnis?: string;
  extension?: string;
  device?: string;
  group?: string;
  skill?: string;
  legalHold?: boolean;
  hasMedia?: boolean;
  minDuration?: number;
  maxDuration?: number;
  sourceId?: string;
  interactionId?: string;
  masterInteractionId?: string;
  segmentId?: string;
  callId?: string;
  fromRetention?: string;
  toRetention?: string;
  hasRetention?: boolean;
  hasTranscription?: boolean;
}

export type FilterPopoverType =
  | "date"
  | "typeahead"
  | "select"
  | "site"
  | "tags"
  | "text"
  | "boolean"
  | "duration"
  | "retention";

export interface FilterChipDef {
  id: string;
  label: string;
  priority: number;
  popoverType: FilterPopoverType;
  hasValue: (f: SearchFilters) => boolean;
  getDisplayValue: (f: SearchFilters, siteMap?: Map<string, string>) => string;
  /**
   * Keys this chip owns. Drives removeFilter (clears all atomically)
   * and matcher dynamic labels (resolves each via getLabel).
   *
   * Convention: for popoverType === "text" chips, ownedKeys is a single-element
   * array and ownedKeys[0] is the canonical text-value key (read/written by the
   * search bar's inline value-entry mode). Composite chips (popoverType: "date" |
   * "duration" | "retention") have multiple ownedKeys but are never text-typed.
   */
  ownedKeys: (keyof SearchFilters)[];
  /** Field used to resolve a customer-overridden display label. Undefined for composite chips. */
  primaryField?: keyof SearchFilters;
  /** Additional matcher terms (lowercased automatically). Defaults to []. */
  synonyms?: string[];
}

function truncate(s: string, max = 16): string {
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

function formatDateRange(from?: string, to?: string): string {
  if (from && to) return `${from.slice(0, 10)} – ${to.slice(0, 10)}`;
  if (from) return `From ${from.slice(0, 10)}`;
  if (to) return `To ${to.slice(0, 10)}`;
  return "All";
}

function formatRetentionDisplay(f: SearchFilters): string {
  if (f.hasRetention === false) return "No";
  if (f.hasRetention === true || f.fromRetention || f.toRetention) {
    const range = formatDateRange(f.fromRetention, f.toRetention);
    if (range === "All") return "Yes";
    return `Yes · ${range}`;
  }
  return "All";
}

function formatDuration(min?: number, max?: number): string {
  if (min != null && max != null) return `${min}s – ${max}s`;
  if (min != null) return `≥ ${min}s`;
  if (max != null) return `≤ ${max}s`;
  return "All";
}

function boolLabel(v?: boolean): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "All";
}

export const FILTER_CHIP_DEFS: FilterChipDef[] = [
  // Priority 1 — always visible
  {
    id: "date",
    label: "Date",
    priority: 1,
    popoverType: "date",
    hasValue: (f) => !!(f.from || f.to),
    getDisplayValue: (f) => formatDateRange(f.from, f.to),
    ownedKeys: ["from", "to"],
    synonyms: ["date", "from", "to", "start", "end", "time", "range"],
  },
  {
    id: "agent",
    label: "Agent",
    priority: 1,
    popoverType: "typeahead",
    hasValue: (f) => !!f.agentName,
    getDisplayValue: (f) => truncate(f.agentName || "All"),
    ownedKeys: ["agentName", "agentNameExactMatch"],
    primaryField: "agentName",
    synonyms: ["agent", "agent name", "name"],
  },
  {
    id: "type",
    label: "Type",
    priority: 1,
    popoverType: "select",
    hasValue: (f) => !!f.interactionType,
    getDisplayValue: (f) => f.interactionType || "All",
    ownedKeys: ["interactionType"],
    primaryField: "interactionType",
    synonyms: ["type", "media"],
  },
  {
    id: "site",
    label: "Sites",
    priority: 1,
    popoverType: "site",
    hasValue: (f) => !!(f.siteIds && f.siteIds.length > 0),
    getDisplayValue: (f, siteMap) => {
      const ids = f.siteIds ?? [];
      if (ids.length === 0) return "All";
      const firstName = siteMap?.get(ids[0]) || ids[0];
      if (ids.length === 1) return truncate(firstName);
      return `${truncate(firstName, 12)}, +${ids.length - 1}`;
    },
    ownedKeys: ["siteIds"],
    primaryField: "siteIds",
    synonyms: ["site", "sites", "location"],
  },
  {
    id: "tags",
    label: "Tags",
    priority: 1,
    popoverType: "tags",
    hasValue: (f) => !!(f.tags && f.tags.length > 0),
    getDisplayValue: (f) => {
      if (!f.tags || f.tags.length === 0) return "All";
      if (f.tags.length === 1) return truncate(f.tags[0]);
      return `${f.tags.length} selected`;
    },
    ownedKeys: ["tags"],
    primaryField: "tags",
    synonyms: ["tag", "tags", "label"],
  },

  // Priority 2 — visible if space
  {
    id: "direction",
    label: "Direction",
    priority: 2,
    popoverType: "select",
    hasValue: (f) => !!f.direction,
    getDisplayValue: (f) => {
      return f.direction ?? "All";
    },
    ownedKeys: ["direction"],
    primaryField: "direction",
    synonyms: ["direction"],
  },
  {
    id: "phoneNumber",
    label: "Phone",
    priority: 2,
    popoverType: "typeahead",
    hasValue: (f) => !!f.phoneNumber,
    getDisplayValue: (f) => truncate(f.phoneNumber || "All"),
    ownedKeys: ["phoneNumber"],
    primaryField: "phoneNumber",
    synonyms: ["phone", "number", "phone number"],
  },
  {
    id: "dnis",
    label: "DNIS",
    priority: 2,
    popoverType: "text",
    hasValue: (f) => !!f.dnis,
    getDisplayValue: (f) => truncate(f.dnis || "All"),
    ownedKeys: ["dnis"],
    primaryField: "dnis",
    synonyms: ["dnis", "number"],
  },
  {
    id: "duration",
    label: "Duration",
    priority: 2,
    popoverType: "duration",
    hasValue: (f) => f.minDuration != null || f.maxDuration != null,
    getDisplayValue: (f) => formatDuration(f.minDuration, f.maxDuration),
    ownedKeys: ["minDuration", "maxDuration"],
    synonyms: ["duration", "length"],
  },

  {
    id: "retention",
    label: "Retention",
    priority: 2,
    popoverType: "retention",
    hasValue: (f) =>
      f.hasRetention != null ||
      f.fromRetention != null ||
      f.toRetention != null,
    getDisplayValue: formatRetentionDisplay,
    ownedKeys: ["fromRetention", "toRetention", "hasRetention"],
    synonyms: ["retention", "date", "expire", "expiry"],
  },

  // Priority 3 — overflow
  {
    id: "extension",
    label: "Extension",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.extension,
    getDisplayValue: (f) => truncate(f.extension || "All"),
    ownedKeys: ["extension"],
    primaryField: "extension",
    synonyms: ["extension"],
  },
  {
    id: "device",
    label: "Device",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.device,
    getDisplayValue: (f) => truncate(f.device || "All"),
    ownedKeys: ["device"],
    primaryField: "device",
    synonyms: ["device"],
  },
  {
    id: "group",
    label: "Group",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.group,
    getDisplayValue: (f) => truncate(f.group || "All"),
    ownedKeys: ["group"],
    primaryField: "group",
    synonyms: ["group"],
  },
  {
    id: "skill",
    label: "Skill",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.skill,
    getDisplayValue: (f) => truncate(f.skill || "All"),
    ownedKeys: ["skill"],
    primaryField: "skill",
    synonyms: ["skill"],
  },
  {
    id: "legalHold",
    label: "Legal Hold",
    priority: 3,
    popoverType: "boolean",
    hasValue: (f) => f.legalHold != null,
    getDisplayValue: (f) => boolLabel(f.legalHold),
    ownedKeys: ["legalHold"],
    primaryField: "legalHold",
    synonyms: ["legal hold", "hold", "legal"],
  },
  {
    id: "hasMedia",
    label: "Has Media",
    priority: 3,
    popoverType: "boolean",
    hasValue: (f) => f.hasMedia != null,
    getDisplayValue: (f) => boolLabel(f.hasMedia),
    ownedKeys: ["hasMedia"],
    primaryField: "hasMedia",
    synonyms: ["has media", "media"],
  },
  {
    id: "sourceId",
    label: "Source ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.sourceId,
    getDisplayValue: (f) => truncate(f.sourceId || "All"),
    ownedKeys: ["sourceId"],
    primaryField: "sourceId",
    synonyms: ["source id", "source", "id"],
  },
  {
    id: "interactionId",
    label: "Interaction ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.interactionId,
    getDisplayValue: (f) => truncate(f.interactionId || "All"),
    ownedKeys: ["interactionId"],
    primaryField: "interactionId",
    synonyms: ["interaction id", "id"],
  },
  {
    id: "masterInteractionId",
    label: "Master ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.masterInteractionId,
    getDisplayValue: (f) => truncate(f.masterInteractionId || "All"),
    ownedKeys: ["masterInteractionId"],
    primaryField: "masterInteractionId",
    synonyms: ["master id", "master", "id"],
  },
  {
    id: "segmentId",
    label: "Segment ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.segmentId,
    getDisplayValue: (f) => truncate(f.segmentId || "All"),
    ownedKeys: ["segmentId"],
    primaryField: "segmentId",
    synonyms: ["segment id", "id"],
  },
  {
    id: "callId",
    label: "Call ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.callId,
    getDisplayValue: (f) => truncate(f.callId || "All"),
    ownedKeys: ["callId"],
    primaryField: "callId",
    synonyms: ["call id", "id"],
  },
  {
    id: "agentId",
    label: "Agent ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.agentId,
    getDisplayValue: (f) => truncate(f.agentId || "All"),
    ownedKeys: ["agentId"],
    primaryField: "agentId",
    synonyms: ["agent id", "id"],
  },
  {
    id: "hasTranscription",
    label: "Has Transcript",
    priority: 3,
    popoverType: "boolean",
    hasValue: (f) => f.hasTranscription != null,
    getDisplayValue: (f) => boolLabel(f.hasTranscription),
    ownedKeys: ["hasTranscription"],
    primaryField: "hasTranscription",
    synonyms: ["transcript", "transcription", "has transcript"],
  },
];

export function chipDisplayLabel(
  chipDef: FilterChipDef,
  getLabel: (field: string) => string,
): string {
  if (chipDef.primaryField) return getLabel(chipDef.primaryField);
  return chipDef.label;
}

export function removeFilter(
  chipDef: FilterChipDef,
  filters: SearchFilters,
): SearchFilters {
  const next = { ...filters };
  for (const key of chipDef.ownedKeys) {
    delete (next as Record<string, unknown>)[key];
  }
  return next;
}
